/* eslint-disable no-use-before-define */

import { NextSeo } from "next-seo";
import { GetStaticPaths, GetStaticProps, InferGetStaticPropsType } from "next";
import { useRouter } from "next/router";
import { useAuthV2 } from "~/hooksV2";
import { wooCommerceAPI } from "~/lib/WooCommerce";
import { ProductCard } from "~/components/commerce/product-card";
// import { Breadcrumbs, Layout } from "~/components/common";
import { useCategoryContext } from "~/context/CategoryContext";
import type { Product, Category } from "~/types/WooCommerce";
import { Wrapper } from "~/ui";
import RawDogFoodContent from "~/components/RawDogFoodContent.jsx";
import CookedDogFoodContent from "~/components/CookedDogFoodContent.jsx";
import { unstable_cache } from "next/cache";
import dynamic from "next/dynamic";

const Breadcrumbs = dynamic(
  () =>
    import("~/components/common/breadcrumbs").then((res) => res.Breadcrumbs),
  {
    ssr: false, // Skip SSR for Breadcrumbs since it might be client-specific
  }
);

const Layout = dynamic(
  () => import("~/components/common/layout").then((res) => res.Layout),
  { ssr: true } // Layout can be SSRed, as it might be important for SEO and the initial page load
);

export default function CategoryPage({ category, products, seo }) {
  const { user } = useAuthV2();
  const router = useRouter();
  const { query } = router;
  const { categoryHandle } = query;

  // const { categoryData } = useCategoryContext();
  // const categoryData1 = categoryData?.find((cat) => cat?.slug === categoryHandle); // {categoryHandle: 'dry-food'}

  const isDisplayTestProduct =
    category?.maintenanceData?.allowed_users?.includes(
      user?.databaseId?.toString()
    ) || false;

  return (
    <>
      <NextSeo
        title={seo?.title}
        description={seo?.description}
        openGraph={{
          title: seo?.title,
          description: seo?.description,
          url: process.env.NEXT_PUBLIC_FRONTEND_URL,
        }}
      />
      <Layout>
        <div className="bg-colorFifteen">
          <div className="mx-auto px-8">
            <Breadcrumbs />
          </div>
          <Wrapper>
            <div className="grid grid-cols-1 gap-8 pt-4 pb-10 text-colorFourteen sm:grid-cols-[2fr_4fr]">
              <div>
                <h1 className="p-0 font-tucker-tub text-header uppercase">
                  {category.name}
                </h1>
              </div>
              <div className="w-full">
                {/* <p className="p-0">{category.description}</p> */}
                <p className="p-0" dangerouslySetInnerHTML={{ __html: category.description }} />
              </div>
            </div>
          </Wrapper>
          <section className="mx-auto px-8">
            <div key={category?.id}>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
                {products
                  ?.filter(
                    (pro: Product) =>
                      pro?.id?.toString() !==
                      category?.maintenanceData?.test_product
                  )
                  .map((product: Product) => (
                    <ProductCard
                      key={product.id}
                      data={product}
                      categoryHandle={category?.slug}
                    />
                  ))}
              </div>
            </div>
          </section>
          {category?.slug === "cooked-dog-food" ? (
            <CookedDogFoodContent />
          ) : category?.slug === "raw-dog-food" ? (
            <RawDogFoodContent />
          ) : null}
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async (context) => {
  const { params } = context;
  const categoryHandle = params?.categoryHandle as string;

  try {
    // Fetch category data to get the category ID
    const categoryResponse = await wooCommerceAPI.get(
      `products/categories?slug=${categoryHandle}`
    );

    if (!categoryResponse.data.length) {
      // console.error(`Category not found for categoryHandle: ${categoryHandle}`);
      return { notFound: true };
    }

    const category = categoryResponse.data[0];
    const categoryId = category.id;

    // Fetch products for the category using the ID
    const productsResponse = await wooCommerceAPI.get(
      `products?category=${categoryId}&status=publish&per_page=100`
    );

    const products = productsResponse.data;

    if (!products) {
      return {
        notFound: true, // Triggers the 404 page
      };
    }

    return {
      props: {
        category,
        products: products || [],
        seo: category?.yoast_head_json || null,
      },
      // revalidate: 3600,
    };
  } catch (error) {
    // console.error(
    //   `Error fetching data for categoryHandle: ${categoryHandle}`,
    //   error
    // );
    return { notFound: true };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_WORDPRESS_ENDPOINT}/wp-json/wc/v3/products/categories`
    );

    if (!response.ok) {
      // console.error('Failed to fetch categories:', response.statusText);
      return { paths: [], fallback: "blocking" };
    }

    const categories = await response.json();

    if (!Array.isArray(categories)) {
      // console.error('Categories is not an array:', categories);
      return { paths: [], fallback: "blocking" };
    }

    const paths = categories.map((category: any) => ({
      params: { categoryHandle: category.slug },
    }));

    return {
      paths,
      fallback: "blocking",
    };
  } catch (error) {
    // console.error('Error fetching category paths:', error);
    return { paths: [], fallback: "blocking" };
  }
};

// import type {
//   InferGetServerSidePropsType,
//   InferGetStaticPropsType,
// } from "next";
// import { NextSeo } from "next-seo";

// import { ProductCard } from "~/components/commerce/product-card";
// import { Breadcrumbs, Layout } from "~/components/common";
// import { useAuthV2 } from "~/hooksV2";
// import { wooCommerceAPI } from "~/lib/WooCommerce";
// import type { Category } from "~/types/WooCommerce";
// import { Wrapper } from "~/ui";
// import { useRouter } from "next/router";
// //import { useCategoryContext } from "~/context/CategoryContext";
// import { useCoockedFoodContext } from "~/context/CoockedFoodCategoryContext";
// import { useRawFoodContext } from "~/context/RawFoodCategoryContext";
// import { useDryFoodContext } from "~/context/DryFoodCategoryContext";
// import { useTreatsFoodContext } from "~/context/TreatsFoodCategoryContext";

// export default function Category({ category }: any) {
//   //   {
//   //   category,
//   // }: InferGetServerSidePropsType<typeof getServerSideProps>
//   const { user } = useAuthV2();
//   const router = useRouter();
//   const { query } = router;
//   const { categoryHandle } = query;

//   //const { categoryData, updateData } = useCategoryContext();
//   const { coockedFoodData } = useCoockedFoodContext();
//   const { rawFoodData } = useRawFoodContext();
//   const { dryFoodData } = useDryFoodContext();
//   const { treatsFoodData } = useTreatsFoodContext();
//   //alert(categoryHandle)

//   let catData =
//     categoryHandle === "cooked-food"
//       ? coockedFoodData
//       : categoryHandle === "raw-food"
//       ? rawFoodData
//       : categoryHandle === "dry-food"
//       ? dryFoodData
//       : treatsFoodData;

//   let categoryData1 = catData?.filter((cat) => cat?.slug === categoryHandle)[0];
//   // let categoryData1 = categoryData?.filter(
//   //   (cat) => cat?.slug === categoryHandle
//   // )[0];

//   //console.log("categoryData========", categoryData);
//   // console.log("coockedFoodData========", coockedFoodData);
//   // console.log("rawFoodData========", rawFoodData);
//   // console.log("dryFoodData========", dryFoodData);
//   // console.log("treatFoodData========", treatsFoodData);
//   //console.log("categoryData1========", categoryData1);

//   const isDisplayTestProduct =
//     categoryData1?.maintenanceData?.allowed_users?.includes(
//       user?.databaseId?.toString()
//     ) || false;

//   const getTitle = (categoryHandle: string) => {
//     switch (categoryHandle) {
//       case "cooked-food":
//         return "Fresh Cooked Dog Food Delivered | Tucker Tub";
//       case "raw-food":
//         return "Fresh Raw Dog Food Delivered | Tucker Tub";
//       case "dry-food":
//         return "Healthy Dry Dog Food | Tucker Tub";
//       case "treats":
//         return "Healthy Dog Treats Delivered | Tucker Tub";
//       default:
//         return "Shop | ${categoryData1?.name} | Tucker Tub";
//     }
//   };

//   const getDescription = (categoryHandle: string) => {
//     switch (categoryHandle) {
//       case "cooked-food":
//         return "Discover Tucker Tub's fresh cooked dog food, delivered directly to you. Perfectly balanced meals for your pet.";
//       case "raw-food":
//         return "Get fresh, raw dog food delivered to your doorstep with Tucker Tub. Quality meals for your pet's health and happiness.";
//       case "dry-food":
//         return "Shop Tucker Tub for healthy dry dog food that supports your pet's diet with balanced nutrition and great taste.";
//       case "treats":
//         return "Indulge your pet with Tucker Tub's healthy dog treats delivered straight to your door. Pure, tasty, and nutritious.";
//       default:
//         return "Shop | ${categoryData1?.name} | Tucker Tub";
//     }
//   };

//   const title = getTitle(categoryHandle as string);
//   const description = getDescription(categoryHandle as string);
//   return (
//     <>
//       {/* <NextSeo
//         title={
//           categoryData1?.name === "Cooked Food"
//             ? "Fresh Cooked Dog Food Delivered | Tucker Tub"
//             : categoryData1?.name === "Raw Food"
//             ? "Fresh Raw Dog Food Delivered | Tucker Tub"
//             : categoryData1?.name === "Dry Food"
//             ? "Healthy Dry Dog Food | Tucker Tub"
//             : categoryData1?.name === "Treats"
//             ? "Healthy Dog Treats Delivered | Tucker Tub"
//             : "Shop | ${categoryData1?.name} | Tucker Tub"
//         }
//         description={
//           categoryData1?.name === "Cooked Food"
//             ? "Discover Tucker Tub's fresh cooked dog food, delivered directly to you. Perfectly balanced meals for your pet."
//             : categoryData1?.name === "Raw Food"
//             ? "Get fresh, raw dog food delivered to your doorstep with Tucker Tub. Quality meals for your pet's health and happiness."
//             : categoryData1?.name === "Dry Food"
//             ? "Shop Tucker Tub for healthy dry dog food that supports your pet's diet with balanced nutrition and great taste."
//             : categoryData1?.name === "Treats"
//             ? "Indulge your pet with Tucker Tub's healthy dog treats delivered straight to your door. Pure, tasty, and nutritious."
//             : "Shop | ${categoryData1?.name} | Tucker Tub"
//         }
//         openGraph={{
//           title:
//             categoryData1?.name === "Cooked Food"
//               ? "Fresh Cooked Dog Food Delivered | Tucker Tub"
//               : categoryData1?.name === "Raw Food"
//               ? "Fresh Raw Dog Food Delivered | Tucker Tub"
//               : categoryData1?.name === "Dry Food"
//               ? "Healthy Dry Dog Food | Tucker Tub"
//               : categoryData1?.name === "Treats"
//               ? "Healthy Dog Treats Delivered | Tucker Tub"
//               : "Shop | ${categoryData1?.name} | Tucker Tub",
//           description:
//             categoryData1?.name === "Cooked Food"
//               ? "Give your dog the gourmet treatment with Tucker Tub's freshly cooked food, delivered directly to you. Perfectly balanced for health and happiness."
//               : categoryData1?.name === "Raw Food"
//               ? "Tucker Tub delivers fresh, raw dog food that's packed with natural goodness. Keep your dog healthy and happy with our convenient home delivery."
//               : categoryData1?.name === "Dry Food"
//               ? "Boost your dog's health with Tucker Tub's premium dry food, packed with essential nutrients. Perfectly formulated for all breeds and sizes."
//               : categoryData1?.name === "Treats"
//               ? "Tucker Tub's healthy dog treats are the perfect way to reward your pet. Nutritious, tasty, and great for keeping them happy and healthy!"
//               : "Shop | ${categoryData1?.name} | Tucker Tub",
//           url: process.env.NEXT_PUBLIC_FRONTEND_URL,
//           type: "website",
//           locale: "en_AU",
//           site_name: "Tucker Tub",
//         }}
//       /> */}
//       <NextSeo
//         title={title}
//         description={description}
//         openGraph={{
//           title: title,
//           description: description,
//           url: process.env.NEXT_PUBLIC_FRONTEND_URL,
//           type: "website",
//           locale: "en_AU",
//           site_name: "Tucker Tub",
//         }}
//       />
//       <Layout>
//         <div className="bg-colorFifteen">
//           <div className="mx-auto px-8">
//             <Breadcrumbs />
//           </div>
//           <Wrapper>
//             <div className="grid grid-cols-1 gap-8 pt-4 pb-10 text-colorFourteen sm:grid-cols-[2fr_4fr]">
//               <div>
//                 <h1 className="p-0 font-tucker-tub text-header uppercase">
//                   {categoryData1?.name}
//                   {/* {category.name} */}
//                 </h1>
//               </div>
//               <div className="w-full">
//                 <p className="p-0">{categoryData1?.description}</p>
//                 {/* <p className="p-0">{category.description}</p> */}
//               </div>
//             </div>
//           </Wrapper>
//           {/* TODO: Hide sort for now */}
//           {/* <div className="inline-grid grid-cols-2 items-start gap-x-8 px-8 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8">
//             <SortSelect />
//             <CategorySelect />
//             <ProteinSelect />
//             <WeightSelect />
//             <TypeSelect />
//             <ClearFilters />
//           </div> */}
//           {/*  */}
//           {/* Product Section */}

//           <section className="mx-auto px-8">
//             <div key={categoryData1?.id}>
//               <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
//                 {isDisplayTestProduct &&
//                   categoryData1?.products
//                     ?.filter(
//                       (pro: any) =>
//                         pro?.id?.toString() ===
//                         categoryData1?.maintenanceData?.test_product
//                     )
//                     .map((product: any) => (
//                       <ProductCard
//                         key={product.id}
//                         data={product}
//                         categoryHandle={categoryData1?.slug}
//                       />
//                     ))}

//                 {categoryData1?.products
//                   ?.filter(
//                     (pro: any) =>
//                       pro?.id?.toString() !==
//                       categoryData1?.maintenanceData?.test_product
//                   )
//                   .map((product: any) => (
//                     <ProductCard
//                       key={product.id}
//                       data={product}
//                       categoryHandle={categoryData1?.slug}
//                     />
//                   ))}
//               </div>
//             </div>
//           </section>
//         </div>
//       </Layout>
//     </>
//   );
// }

// // export async function getServerSideProps(props: any) {
// //   const { params } = props;
// //   const { categoryHandle } = params;
// //   //console.log("categoryHandle:", categoryHandle);

// //   const { data: categoriesData } = await wooCommerceAPI.get(
// //     `products/categories?slug=${categoryHandle}`
// //   );

// //   const categoriesWithoutUncategorized = categoriesData.filter(
// //     (category: Category) => category.name.toLowerCase() !== "uncategorised"
// //   );

// //   const categories = categoriesWithoutUncategorized.map(
// //     async (category: Category) => {
// //       const { id } = category;
// //       const { data: productsData } = await wooCommerceAPI.get(
// //         `products?category=${id}&status=publish&per_page=100`
// //       );

// //       const response = await fetch(
// //         `${process.env.NEXT_PUBLIC_WORDPRESS_ENDPOINT}/wp-json/tuckertub/v1/maintanance_mode/`
// //       );

// //       const data = await response.json();

// //       return {
// //         ...category,
// //         products: productsData,
// //         maintenanceData: data,
// //       };
// //     }
// //   );

// //   const categoriesPromise = Promise.all(categories);

// //   const categoryData = await categoriesPromise;

// //   return {
// //     props: {
// //       category: categoryData[0],
// //     },
// //   };
// // }

// // export async function getStaticPaths() {
// //   const { data: categoriesData } = await wooCommerceAPI.get('products/categories')

// //   const categoriesWithoutUncategorized = categoriesData.filter(
// //     (category: Category) => category.name.toLowerCase() !== 'uncategorised'
// //   )

// //   const categories = categoriesWithoutUncategorized.map((category: Category) => ({
// //     params: {
// //       categoryHandle: category.slug
// //     }
// //   }))

// //   return {
// //     paths: categories,
// //     fallback: false
// //   }
// // }
